import { useEffect, useState } from 'react'
import { ReleaseNotesList } from '@/components/release-notes/release-notes-list'
import { Skeleton } from '@/components/ui/skeleton'
import { getReleaseNotes, type ReleaseNote } from '@/lib/release-notes'

/**
 * Loading state component for release notes list
 *
 * Displays skeleton loaders while release notes are being fetched.
 */
function LoadingState() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-5 w-96" />
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Skeleton key={i} className="h-48 w-full rounded-lg" />
        ))}
      </div>
    </div>
  )
}

/**
 * Release notes index page component
 *
 * Fetches and displays a list of all release notes.
 * This is a public page that does not require authentication.
 */
export function ReleaseNotesPage() {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        const notes = await getReleaseNotes()
        setReleaseNotes(notes)
      } catch (error) {
        console.error('Failed to fetch release notes:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchReleaseNotes()
  }, [])

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <div className="container max-w-7xl mx-auto p-6">
          {loading ? <LoadingState /> : <ReleaseNotesList releaseNotes={releaseNotes} showFooter={false} />}
        </div>
      </div>
      {/* Fixed footer at bottom */}
      {!loading && releaseNotes.length > 0 && (
        <div className="border-t bg-background">
          <div className="container max-w-7xl mx-auto p-4">
            <p className="text-center text-sm text-muted-foreground">
              Showing {releaseNotes.length} {releaseNotes.length === 1 ? 'release' : 'releases'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
